/**
 * t373u: HR documents and right to work on a phone (your record and someone else's), and People › HR expiring soon.
 * The iOS app mirrors this screen for screen (apps/mobile/src/settings/hrDocs.tsx). Picking a file offers the camera,
 * photos or files (the browser's own chooser); opening one goes through an audited 60-second link.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Eye, EyeOff } from 'lucide-react';
import { HR_DOC_TYPE_SHORT, hrDocLine, hrExpiringLine, hrToday, rtwRows, type HrDocument, type HrDocumentsView } from '@tradeoutlet/shared';
import { errorMessage } from '@/lib/api';
import { useUi } from '@/lib/store';
import { Spinner } from '@/components/ui';
import { DeleteDoc, DocForm, docByLine, RtwForm } from '@/features/hr/HrDocuments';
import { useHrDocActions, useHrDocs, useHrExpiring, useShareCodeReveal } from '@/features/hr/docs';
import { BottomSheet } from '../BottomSheet';
import { card, ghostBtn, head } from './PersonSettings';

const rowBtn = 'flex min-h-[52px] w-full items-center gap-2 border-b border-slate-100 py-2 text-left last:border-0 active:bg-slate-50 dark:border-slate-800 dark:active:bg-slate-800';

function RtwSection({ view }: { view: HrDocumentsView }) {
  const rv = useShareCodeReveal(view.user_id);
  const [adding, setAdding] = useState(false);
  const cur = view.rtw[0];
  if (!cur && !view.can.record_rtw) return null;
  return (
    <section data-testid="m-hr-rtw">
      <div className={clsx(head, 'flex items-center')}>
        <span className="flex-1">Right to work</span>
        {view.can.record_rtw && <button type="button" className="-my-2 h-9 px-2 text-sm font-medium normal-case tracking-normal text-slate-700 dark:text-slate-200" onClick={() => setAdding(true)} data-testid="m-hr-rtw-add">{cur ? 'New check' : 'Record'}</button>}
      </div>
      <div className={card}>
        {!cur && <p className="py-2 text-[15px] text-slate-500">No check recorded yet.</p>}
        {cur && rtwRows(cur).map(r => {
          const shown = r.key === 'share_code' ? rv.value(cur.id) : null;
          return (
            <div key={r.key} className="flex items-center gap-2 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-slate-500">{r.label}</div>
                <div className={clsx('break-words text-[15px]', shown && 'font-mono')} data-testid={`m-hr-rtw-value-${r.key}`}>{shown ?? r.value}</div>
                {shown && <div className="text-xs text-slate-500">Hidden again in {rv.left}s</div>}
              </div>
              {r.key === 'share_code' && view.can.reveal_share_code && <button type="button" className="flex h-11 min-w-11 items-center justify-center gap-1 rounded-full px-2 text-sm text-slate-600 active:bg-slate-100 dark:text-slate-300 dark:active:bg-slate-800"
                onClick={() => (shown ? rv.hide() : void rv.reveal(cur.id))} data-testid={`m-hr-rtw-${shown ? 'hide' : 'show'}`}>
                {shown ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}{shown ? 'Hide' : 'Show'}</button>}
            </div>
          );
        })}
      </div>
      {view.rtw.length > 1 && <p className="px-4 pt-1 text-xs text-slate-500">{view.rtw.length - 1} earlier {view.rtw.length === 2 ? 'check' : 'checks'} on a computer</p>}
      <BottomSheet open={adding} onClose={() => setAdding(false)} title="Right-to-work check" testId="m-hr-rtw-sheet">
        {adding && <div className="px-4 pb-4"><RtwForm view={view} onDone={() => setAdding(false)} /></div>}
      </BottomSheet>
    </section>
  );
}

function DocSheet({ view, doc, onClose }: { view: HrDocumentsView; doc: HrDocument | null; onClose: () => void }) {
  const a = useHrDocActions(view.user_id);
  const [mode, setMode] = useState<'edit' | 'delete' | null>(null);
  const close = () => { setMode(null); onClose(); };
  const by = doc ? docByLine(doc, view) : '';
  return (
    <BottomSheet open={Boolean(doc)} onClose={close} title={doc ? HR_DOC_TYPE_SHORT[doc.type] : ''} testId="m-hr-doc-sheet">
      {doc && <div className="px-4 pb-4">
        {mode === 'edit' ? <DocForm view={view} doc={doc} onDone={close} />
          : mode === 'delete' ? <DeleteDoc view={view} doc={doc} onDone={close} />
          : <>
            <p className="text-sm text-slate-500">{hrDocLine(doc, hrToday(), !view.self)}{by ? ` · ${by}` : ''}</p>
            {doc.notes && <p className="mt-1 text-sm">{doc.notes}</p>}
            <div className="mt-3 grid gap-2">
              <button type="button" className={clsx(ghostBtn, 'w-full')} disabled={a.busy} onClick={() => void a.open(doc)} data-testid="m-hr-doc-open">Open</button>
              <button type="button" className={clsx(ghostBtn, 'w-full')} disabled={a.busy} onClick={() => void a.open(doc, true)}>Download</button>
              {doc.can_edit && <button type="button" className={clsx(ghostBtn, 'w-full')} onClick={() => setMode('edit')} data-testid="m-hr-doc-edit">Edit details</button>}
              {doc.can_delete && <button type="button" className={clsx(ghostBtn, 'w-full text-rose-700')} onClick={() => setMode('delete')} data-testid="m-hr-doc-delete">Delete</button>}
            </div>
          </>}
      </div>}
    </BottomSheet>
  );
}

function DocsSection({ view }: { view: HrDocumentsView }) {
  const [open, setOpen] = useState<HrDocument | null>(null);
  const [adding, setAdding] = useState(false);
  const today = hrToday();
  return (
    <section data-testid="m-hr-docs">
      <div className={head}>Documents</div>
      {view.asked.length > 0 && <p className="px-4 pb-1 text-sm text-slate-500" data-testid="m-hr-doc-asked">{view.self ? 'HR asks you to upload' : 'Still to upload'}: {view.asked.map(t => HR_DOC_TYPE_SHORT[t]).join(', ')}</p>}
      <div className={card}>
        {view.documents.map(d => (
          <button key={d.id} type="button" className={rowBtn} onClick={() => setOpen(d)} data-testid={`m-hr-doc-${d.id}`}>
            <span className="min-w-0 flex-1"><span className="block text-[15px]">{HR_DOC_TYPE_SHORT[d.type]}</span>
              <span className="block truncate text-xs text-slate-500">{hrDocLine(d, today, !view.self)}</span></span>
            <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />
          </button>
        ))}
        {!view.documents.length && <p className="py-2 text-[15px] text-slate-500">No documents yet.</p>}
      </div>
      {view.can.upload && <div className="px-3 pt-2"><button type="button" className={clsx(ghostBtn, 'w-full')} onClick={() => setAdding(true)} data-testid="m-hr-doc-add">Upload a document</button></div>}
      <DocSheet view={view} doc={open} onClose={() => setOpen(null)} />
      <BottomSheet open={adding} onClose={() => setAdding(false)} title="Upload a document" testId="m-hr-doc-upload">
        {adding && <div className="px-4 pb-4"><DocForm view={view} onDone={() => setAdding(false)} /></div>}
      </BottomSheet>
    </section>
  );
}

export function PhoneHrDocuments({ who }: { who: string }) {
  const q = useHrDocs(who);
  if (q.isLoading) return <div className="p-4"><Spinner /></div>;
  if (!q.data) return null;
  return <div data-testid="m-hr-documents"><RtwSection view={q.data} /><DocsSection view={q.data} /></div>;
}

/** Settings › People › HR expiring soon (the reminder link on a phone). */
export function HrExpiringPage() {
  const q = useHrExpiring();
  const nav = useNavigate();
  const openPerson = useUi(s => s.openPerson);
  const today = hrToday();
  if (q.isLoading) return <div className="p-6"><Spinner /></div>;
  if (!q.data) return <p className="p-4 text-sm text-slate-500">{errorMessage(q.error)}</p>;
  return (
    <div className="flex-1 overflow-y-auto pb-6" data-testid="m-hr-expiring">
      <div className={clsx(card, 'mt-3')}>
        {q.data.map(r => (
          <div key={`${r.kind}:${r.id}`} className="flex min-h-[56px] items-center gap-2 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800" data-testid={`m-hr-expiring-${r.id}`}>
            <div className="min-w-0 flex-1">
              <button type="button" className="text-left text-[15px] font-medium" onClick={() => openPerson(r.user_id)}>{r.display_name}</button>
              <div className="text-sm">{r.label}</div>
              <div className="text-xs text-slate-500">{hrExpiringLine(r, today)}</div>
            </div>
            <button type="button" aria-label={`Open ${r.display_name}’s personal details`} className="flex h-11 w-11 items-center justify-center rounded-full active:bg-slate-100 dark:active:bg-slate-800"
              onClick={() => nav(`/settings/people/users/${r.user_id}?tab=hr`)}><ChevronRight className="h-5 w-5 text-slate-400" aria-hidden /></button>
          </div>
        ))}
        {!q.data.length && <p className="py-3 text-[15px] text-slate-500">Nothing expires in the next 90 days.</p>}
      </div>
      <p className="px-4 pt-2 text-xs text-slate-500">Reminders go to the people who manage HR documents 60, 30 and 7 days before.</p>
    </div>
  );
}
